/* ═══════════════════════════════════════════════════════════════
   Synchronisation — Entscheidungslogik

   Die Anwendung arbeitet ohne Netz weiter: der Browser hält eine
   vollständige lokale Kopie und schiebt sie erst dann zum Server,
   wenn dieser wieder erreichbar ist. Damit wird der Fall unvermeid-
   lich, dass zwei Stände auseinanderlaufen — hier steht, wie er
   erkannt wird.

   Zusammengeführt wird nur mit bekannter Basis (kern/dreiwege.js):
   Dann ist belegt, wer was geändert hat. Echte Widersprüche — ein
   Wein, der hier gelöscht und dort um einen Jahrgang ergänzt wurde —
   entscheidet weiterhin der Nutzer, auf Basis von Kennzahlen, die er
   wiedererkennt (Flaschen, Weine, letzte Bewegung). Ohne Basis gibt
   es keine automatische Verschmelzung: stiller Verlust ist bei einem
   Kellerbuch schlimmer als eine Rückfrage.
   ═══════════════════════════════════════════════════════════════ */


/** Was ein Synchronisationslauf zu tun hat:

    - `aktuell`        beide Seiten stimmen überein, nichts zu tun
    - `hochladen`      nur lokal wurde geändert
    - `herunterladen`  nur auf dem Server wurde geändert
    - `konflikt`       beide Seiten wurden geändert → Rückfrage

    `basisVersion` ist die Serverversion, auf der die lokale Kopie
    aufsetzt; weicht die tatsächliche Serverversion davon ab, hat
    zwischenzeitlich ein anderes Gerät gespeichert. */
function syncFall({ lokalGeaendert, basisVersion, serverVersion, serverHatStand = true, lokalHatDaten = true }) {
  /* Version 0 heißt: der Server kennt noch gar keinen Stand (frisch
     angelegtes Konto). Dann kann es keinen Konflikt geben — was da
     ist, geht hinauf. */
  if (!serverHatStand) return lokalHatDaten ? "hochladen" : "aktuell";
  const serverGeaendert = (serverVersion || 0) !== (basisVersion || 0);
  if (lokalGeaendert && serverGeaendert) return "konflikt";
  if (lokalGeaendert) return "hochladen";
  if (serverGeaendert) return "herunterladen";
  return "aktuell";
}


/** Kennzahlen eines Kellerbuchstandes — die Grundlage, auf der im
    Konfliktfall entschieden wird. Absichtlich Größen, die ein Nutzer
    im Kopf hat: „bei mir waren es 214 Flaschen". */
function standKennzahlen(db) {
  const leer = {
    weine: 0, winzer: 0, regionen: 0, jahrgaenge: 0, flaschen: 0,
    bewegungen: 0, kritiken: 0, weinkarten: 0, letzteBewegung: null,
  };
  if (!db) return leer;

  let jahrgaenge = 0;
  let flaschen = 0;
  for (const w of db.weine || []) {
    for (const jg of w.jahrgaenge || []) {
      jahrgaenge++;
      for (const anzahl of Object.values(jg.bestaende || {})) flaschen += anzahl || 0;
    }
  }

  /* Größter Zeitstempel über Bewegungen UND Entnahmen: beide Listen
     überlappen zwar, aber ältere Stände kennen nur `entnahmen`. */
  let letzte = null;
  for (const liste of [db.bewegungen || [], db.entnahmen || []]) {
    for (const b of liste) {
      if (b && b.datum && (letzte === null || String(b.datum) > letzte)) letzte = String(b.datum);
    }
  }

  return {
    weine: (db.weine || []).length,
    winzer: (db.winzer || []).length,
    regionen: (db.regions || []).length,
    jahrgaenge,
    flaschen,
    bewegungen: (db.bewegungen || []).length,
    kritiken: (db.kritiken || []).length,
    weinkarten: (db.weinkarten || []).length,
    letzteBewegung: letzte,
  };
}


/* Reihenfolge und Beschriftung der Gegenüberstellung. Der deutsche
   Text ist zugleich der t()-Schlüssel (Konvention des Projekts). */
const KENNZAHL_FELDER = [
  ["flaschen", "Flaschen"],
  ["weine", "Weine"],
  ["jahrgaenge", "Jahrgänge"],
  ["winzer", "Winzer"],
  ["regionen", "Regionen"],
  ["bewegungen", "Bewegungen"],
  ["kritiken", "Kritiken"],
  ["weinkarten", "Weinkarten"],
];


/** Zwei Stände gegenüberstellen: je Kennzahl eine Zeile mit beiden
    Werten und der Angabe, ob sie sich unterscheiden. Die Zeilen
    werden in der Reihenfolge von KENNZAHL_FELDER geliefert. */
function vergleicheStaende(lokalDb, serverDb) {
  const a = standKennzahlen(lokalDb);
  const b = standKennzahlen(serverDb);
  return KENNZAHL_FELDER.map(([schluessel, label]) => ({
    schluessel,
    label,
    lokal: a[schluessel],
    server: b[schluessel],
    abweichung: a[schluessel] !== b[schluessel],
  }));
}


/** Ist der Stand überhaupt der Rede wert? Ein leeres Kellerbuch
    hochzuladen und damit einen gefüllten Serverstand zu überschreiben
    ist der einzige wirklich gefährliche Ausgang — die Oberfläche
    warnt davor gesondert. */
const standIstLeer = (db) => {
  const k = standKennzahlen(db);
  return k.weine === 0 && k.winzer === 0 && k.bewegungen === 0;
};


export { KENNZAHL_FELDER, standIstLeer, standKennzahlen, syncFall, vergleicheStaende };
